<template>
  <div class="chart-card">
    <div class="chart-header">
      <h4>Most Popular Devices</h4>
      <div class="info-tooltip">
        <span class="info-icon">ℹ️</span>
        <div class="tooltip-content">
          Counts how many times each device model has been checked out. Devices with higher checkout frequency appear at the top.
        </div>
      </div>
    </div>
    <div class="chart-content">
      <div v-for="device in popularDevices" :key="device.model" class="bar-item">
        <span class="bar-label">{{ device.model }}</span>
        <div class="bar-container">
          <div class="bar" :style="{ width: device.percentage + '%' }"></div>
          <span class="bar-value">{{ device.count }} uses</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { COLORS } from '@/styles/brand'

const popularDevices = ref([])

const props = defineProps({
  devices: Array
})

const calculatePopularDevices = () => {
  const modelCounts = {}
  props.devices.forEach(device => {
    if (device.lastTakenBy) {
      const model = device.model || 'Unknown Model'
      modelCounts[model] = (modelCounts[model] || 0) + 1
    }
  })
  
  const maxCount = Math.max(...Object.values(modelCounts), 1)
  popularDevices.value = Object.entries(modelCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .map(([model, count]) => ({
      model,
      count,
      percentage: (count / maxCount) * 100
    }))
}

watch(() => props.devices, () => {
  if (props.devices) {
    calculatePopularDevices()
  }
}, { immediate: true })
</script>

<style scoped>
.chart-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  border-radius: 16px;
  position: relative;
  overflow: visible;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-header h4 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.info-tooltip {
  position: relative;
  display: inline-block;
}

.info-icon {
  cursor: help;
  font-size: 1rem;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.info-icon:hover {
  opacity: 1;
}

.tooltip-content {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.95);
  color: v-bind('COLORS.PRIMARY.WHITE');
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 280px;
  z-index: 9999;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.tooltip-content::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 12px;
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.95);
}

.info-tooltip:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.bar-item {
  margin-bottom: 12px;
}

.bar-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  display: block;
  margin-bottom: 4px;
}

.bar-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bar {
  height: 8px;
  background: v-bind('COLORS.PRIMARY.RED');
  border-radius: 4px;
  min-width: 4px;
}

.bar-value {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-size: 0.9rem;
  font-weight: 600;
}
</style>